import { useEffect, useRef, useState } from "react";
import { Platform, Pressable, Text, TextInput, View, type TextStyle, type ViewStyle } from "react-native";

import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import { updateTeamPitch } from "../../lib/teams";


const DESC_MAX = 280;
const LF_MAX = 140;

/**
 * The Pitch section on the team workspace page.
 *
 * Captain mode: two textareas with char counters that auto-save (debounced
 * 1.5s after the last edit). The amber save indicator above the inputs
 * mirrors the profile-page pattern.
 *
 * Member mode: read-only — you see what the captain wrote so the team feels
 * like a place, not a row in a database.
 *
 * Empty state for captains: a gentle nudge with examples; the section
 * collapses entirely for members when both fields are empty.
 */
export function TeamPitchSection({
  teamId,
  isCaptain,
  initialDescription,
  initialLookingFor,
}: {
  teamId: string;
  isCaptain: boolean;
  initialDescription: string | null;
  initialLookingFor: string | null;
}) {
  const [description, setDescription] = useState<string>(initialDescription ?? "");
  const [lookingFor, setLookingFor] = useState<string>(initialLookingFor ?? "");
  const [saveState, setSaveState] = useState<"idle" | "dirty" | "saving" | "saved">("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const baselineRef = useRef<string>(`${initialDescription ?? ""}|${initialLookingFor ?? ""}`);
  const savedFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Whenever the props change (parent reloaded), realign baseline.
  useEffect(() => {
    setDescription(initialDescription ?? "");
    setLookingFor(initialLookingFor ?? "");
    baselineRef.current = `${initialDescription ?? ""}|${initialLookingFor ?? ""}`;
    setSaveState("idle");
  }, [initialDescription, initialLookingFor]);

  // Mark dirty whenever the form differs from baseline.
  useEffect(() => {
    if (!isCaptain) return;
    const cur = `${description}|${lookingFor}`;
    if (cur === baselineRef.current) {
      // Reverted — only flip back to idle if we're not currently saving.
      setSaveState((s) => (s === "saving" ? s : s === "saved" ? "saved" : "idle"));
      return;
    }
    setSaveState("dirty");
  }, [description, lookingFor, isCaptain]);

  // Debounced auto-save.
  useEffect(() => {
    if (!isCaptain || saveState !== "dirty") return;
    const t = setTimeout(async () => {
      setSaveState("saving");
      const { error } = await updateTeamPitch(teamId, {
        description: description.trim() || null,
        looking_for: lookingFor.trim() || null,
      });
      if (error) {
        // Roll back to dirty so the user can retry; surface the error in the
        // hint line below.
        setSaveState("dirty");
        return;
      }
      baselineRef.current = `${description}|${lookingFor}`;
      setLastSavedAt(Date.now());
      setSaveState("saved");
      if (savedFlashTimerRef.current) clearTimeout(savedFlashTimerRef.current);
      savedFlashTimerRef.current = setTimeout(() => {
        setSaveState((s) => (s === "saved" ? "idle" : s));
      }, 2500);
    }, 1500);
    return () => clearTimeout(t);
  }, [saveState, description, lookingFor, teamId, isCaptain]);

  // Read-only mode for members (or empty state hide).
  if (!isCaptain) {
    if (!description.trim() && !lookingFor.trim()) return null;
    return (
      <View className="mt-8">
        <Text
          className="text-[10px] font-bold uppercase tracking-widest-3"
          style={{ fontFamily: FONTS.bodyBold, color: COLORS.AMBER.hex, letterSpacing: 2 }}
        >
          Pitch
        </Text>
        <Text
          className="text-[24px] leading-tight text-forest"
          style={{ fontFamily: FONTS.displayMedium }}
        >
          About this team
        </Text>
        <View
          className="mt-3 overflow-hidden rounded-[6px] border bg-white px-5 py-4"
          style={{ borderColor: COLORS.SAND_DARK.hex }}
        >
          {description.trim() ? (
            <Text
              className="text-[14px] leading-[22px] text-forest"
              style={{ fontFamily: FONTS.body }}
            >
              {description}
            </Text>
          ) : null}
          {lookingFor.trim() ? (
            <View className={description.trim() ? "mt-3" : ""}>
              <Text
                className="text-[10px] font-bold uppercase"
                style={{ fontFamily: FONTS.bodyBold, color: COLORS.AMBER.hex, letterSpacing: 1.4 }}
              >
                Looking for
              </Text>
              <Text
                className="mt-0.5 text-[13.5px] leading-5"
                style={{ fontFamily: FONTS.body, color: "rgba(28,43,26,0.78)" }}
              >
                {lookingFor}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
    );
  }

  // Captain editor.
  return (
    <View className="mt-8">
      <View className="flex-row items-baseline justify-between gap-2">
        <View>
          <Text
            className="text-[10px] font-bold uppercase tracking-widest-3"
            style={{ fontFamily: FONTS.bodyBold, color: COLORS.AMBER.hex, letterSpacing: 2 }}
          >
            Pitch
          </Text>
          <Text
            className="text-[24px] leading-tight text-forest"
            style={{ fontFamily: FONTS.displayMedium }}
          >
            What's this team about?
          </Text>
        </View>
        <SaveIndicator state={saveState} lastSavedAt={lastSavedAt} />
      </View>

      <Text
        className="mt-1 text-[12.5px] leading-[18px]"
        style={{ fontFamily: FONTS.bodyLight, color: STUDENT_THEME.textOnSurfaceDim }}
      >
        Other students see this when browsing teams in the case. A short pitch helps you attract the right teammates.
      </Text>

      <View className="mt-3 gap-4">
        <PitchField
          label="Description"
          placeholder="e.g. Three finance majors going hard on the analytics side. We meet Tuesdays at 7. Aiming for top 5."
          value={description}
          onChangeText={setDescription}
          max={DESC_MAX}
          rows={3}
        />
        <PitchField
          label="Looking for"
          placeholder="e.g. A designer to round us out — slides, charts, polish."
          value={lookingFor}
          onChangeText={setLookingFor}
          max={LF_MAX}
          rows={2}
        />
      </View>
    </View>
  );
}

function PitchField({
  label,
  placeholder,
  value,
  onChangeText,
  max,
  rows,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChangeText: (s: string) => void;
  max: number;
  rows: number;
}) {
  const remaining = max - value.length;
  const overLimit = remaining < 0;
  const counterColor =
    overLimit ? STUDENT_THEME.destructive : remaining < 20 ? COLORS.AMBER.hex : STUDENT_THEME.textOnSurfaceFaint;

  return (
    <View>
      <View className="mb-1 flex-row items-baseline justify-between">
        <Text
          className="text-[10.5px] font-bold uppercase"
          style={{ fontFamily: FONTS.bodyBold, color: STUDENT_THEME.textOnSurfaceDim, letterSpacing: 1.4 }}
        >
          {label}
        </Text>
        <Text
          className="text-[10.5px]"
          style={{ fontFamily: FONTS.bodyMedium, color: counterColor }}
        >
          {value.length} / {max}
        </Text>
      </View>
      <TextInput
        value={value}
        onChangeText={(v) => onChangeText(v.length > max ? v.slice(0, max) : v)}
        placeholder={placeholder}
        placeholderTextColor="rgba(26,26,24,0.32)"
        multiline
        textAlignVertical="top"
        style={[
          {
            minHeight: rows * 22 + 14,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: overLimit ? "rgba(192,57,43,0.45)" : COLORS.SAND_DARK.hex,
            backgroundColor: STUDENT_THEME.surfaceRaisedBg,
            paddingHorizontal: 12,
            paddingVertical: 10,
            fontFamily: FONTS.body,
            fontSize: 14,
            color: COLORS.FOREST.hex,
            ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
          } as unknown as TextStyle,
        ]}
      />
    </View>
  );
}

function SaveIndicator({
  state,
  lastSavedAt,
}: {
  state: "idle" | "dirty" | "saving" | "saved";
  lastSavedAt: number | null;
}) {
  let label: string;
  let color: string;
  let dot: string | null;
  if (state === "saving") {
    label = "Saving…";
    color = COLORS.AMBER.hex;
    dot = COLORS.AMBER.hex;
  } else if (state === "saved") {
    label = "Saved";
    color = "#5FAD56";
    dot = "#5FAD56";
  } else if (state === "dirty") {
    label = "Saves automatically";
    color = COLORS.AMBER.hex;
    dot = COLORS.AMBER.hex;
  } else if (lastSavedAt) {
    const t = new Date(lastSavedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    label = `Saved at ${t}`;
    color = STUDENT_THEME.textOnSurfaceDim;
    dot = "rgba(95,173,86,0.55)";
  } else {
    label = "Auto-saves as you type";
    color = STUDENT_THEME.textOnSurfaceDim;
    dot = null;
  }
  return (
    <Pressable disabled style={{ alignSelf: "flex-start" }}>
      <View
        className="flex-row items-center gap-2 rounded-full border px-3 py-1"
        style={{ borderColor: "rgba(28,43,26,0.12)", backgroundColor: "rgba(28,43,26,0.03)" }}
      >
        {dot ? (
          <View
            style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: dot }}
          />
        ) : null}
        <Text
          className="text-[10.5px]"
          style={{
            fontFamily: state === "saving" || state === "saved" ? FONTS.bodySemiBold : FONTS.bodyMedium,
            color,
            letterSpacing: 0.2,
          }}
        >
          {label}
        </Text>
      </View>
    </Pressable>
  );
}
